import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { FC, useEffect, useState } from "react";
import { BsArchive } from "react-icons/bs";
import { HiEllipsisVertical } from "react-icons/hi2";
import { LuMerge } from "react-icons/lu";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { toast } from "react-toastify";

import { Pages } from "../../../../app/constants/routes";
import { generateRoutePath } from "../../../../app/constants/routes/utils";
import { Badge } from "../../../../components/badge/Badge";
import Button from "../../../../components/button/Button";
import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import IndividualContactSvg from "../../../../components/svg/IndividualContactSvg";
import { inactiveStatusId } from "../../../../constants/constants";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useActivateContactMutation,
  useArchiveContactMutation,
  useDeleteContactMutation,
  useGetContactQuery,
} from "../../../../slices/ContactsSlice";
import { CustomLink } from "../../../../support/CustomLink";
import { GetPageTitle } from "../../../../support/ScrollToTop";
import { primaryEmailOf, tabLabel } from "../../../../utils/tabCounts";
import { ContactDeleteConfirmModal } from "../../components/ContactDeleteConfirmModal";
import { ContactStatusConfirmModal } from "../../components/ContactStatusConfirmModal";
import { UpdateAvatarModal } from "../../modals/UpdateAvatarModal";
import { MergeContactModal } from "../../refactor/modals/MergeContactModal";
import { useClientTabCounts } from "../../useClientTabCounts";

export const ContactView: FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();

  const { data: contact, isLoading } = useGetContactQuery({
    customerId: customerId || "",
    orgId: getLinkedFirm()?.orgId || "",
  });
  const [deleteContact, { isLoading: isLoadingDelete }] =
    useDeleteContactMutation();
  const [activateContact, { isLoading: isLoadingActivate }] =
    useActivateContactMutation();
  const [archiveContact, { isLoading: isLoadingArchive }] =
    useArchiveContactMutation();

  // Each tab's count reads the same data as the tab (Roadmap 1842).
  const counts = useClientTabCounts(customerId || "", primaryEmailOf(contact));

  const [isOpenMerge, setIsOpenMerge] = useState(false);
  const [isOpenActivation, setIsOpenActivation] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isUpdateAvatar, setIsUpdateAvatar] = useState(false);

  const { profile, works, files, time, notes, billing, emails, portal } =
    Pages.contact.children;

  const ProfileComponent = profile.component;
  const WorksComponent = works.component;
  const FilesComponent = files.component;
  const TimeComponent = time.component;
  const NotesComponent = notes.component;
  const BillingComponent = billing.component;
  const EmailsComponent = emails.component;
  const PortalComponent = portal.component;

  const isActive = contact?.contactStatusTypeEnumId !== inactiveStatusId;
  const contactFullName = [
    contact?.firstName,
    contact?.middleName,
    contact?.lastName,
  ]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    document.title = GetPageTitle(Pages.contact.label);
  }, []);

  // Old links opened the profile with the contact id instead of the client
  // (customer) id; the server now finds the client either way, so move to the
  // right address so the tabs (files, work, ...) load too (QA fix 2026-10-21).
  const location = useLocation();
  useEffect(() => {
    const realId = contact?.customerId;
    if (!realId || !customerId) return;
    if (realId.toLowerCase() === customerId.toLowerCase()) return;
    navigate(location.pathname.replace(customerId, realId) + location.search, {
      replace: true,
    });
  }, [
    contact?.customerId,
    customerId,
    location.pathname,
    location.search,
    navigate,
  ]);

  if (isLoading || isLoadingActivate || isLoadingArchive) {
    return <LottieLoading />;
  }

  if (!contact) {
    return null;
  }

  return (
    <>
      <div className={"grid grid-cols-1 justify-center"}>
        <div className={"w-full max-w-[1920px]"}>
          <div className={"flex w-full items-center gap-4"}>
            <div
              /*onClick={() => setIsUpdateAvatar(true)}*/
              className={
                "flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full bg-[#002466]"
              }>
              {contact.imageURL ? (
                <img src={contact.imageURL} alt={"avatar"} />
              ) : (
                <IndividualContactSvg className={"text-white"} />
              )}
            </div>
            <div className={"flex min-w-0 flex-1 items-end"}>
              {/* Long names: one line, cut with "…" + full name on hover
                  (QA batch 8; it wrapped mid-word). */}
              <h1 className={"flex min-w-0 items-center gap-1 text-2xl"}>
                <span className={"truncate"} title={contactFullName}>
                  {contactFullName}
                </span>
                {contact?.isSyncedWithQBO && (
                  <img
                    src={
                      "https://cdn.shopify.com/app-store/listing_images/a9755168ba32bb3e7fa95c947d11069e/icon/CPafhOnvuf0CEAE=.png"
                    }
                    height={"24px"}
                    width={"24px"}
                  />
                )}
              </h1>
              {!isActive && (
                <Badge
                  label={"Archived"}
                  variant={"archived"}
                  className={"ml-2 shrink-0"}
                />
              )}
            </div>
          </div>
          <div className={"my-4 flex w-full justify-between"}>
            <div className={"tabs"}>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.contact.path,
                    customerId || "",
                    profile.path,
                  ]),
                }}>
                {profile.label}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.contact.path,
                    customerId || "",
                    works.path,
                  ]),
                }}>
                {tabLabel(works.label, counts.works)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.contact.path,
                    customerId || "",
                    files.path,
                  ]),
                }}>
                {tabLabel(files.label, counts.files)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.contact.path,
                    customerId || "",
                    time.path,
                  ]),
                }}>
                {tabLabel(time.label, counts.time)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.contact.path,
                    customerId || "",
                    notes.path,
                  ]),
                }}>
                {tabLabel(notes.label, counts.notes)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.contact.path,
                    customerId || "",
                    billing.path,
                  ]),
                }}>
                {billing.label}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.contact.path,
                    customerId || "",
                    emails.path,
                  ]),
                }}>
                {tabLabel(emails.label, counts.emails)}
              </CustomLink>
              <CustomLink
                to={{
                  pathname: generateRoutePath([
                    Pages.contact.path,
                    customerId || "",
                    portal.path,
                  ]),
                }}>
                {portal.label}
              </CustomLink>
            </div>
            <div>
              <div className={"dropdown dropdown-end"}>
                <div>
                  <Button
                    label={""}
                    colorType={"ghost"}
                    size={"custom"}
                    icon={<HiEllipsisVertical size={24} />}
                    extraClasses={"min-h-fit h-fit w-fit p-1"}
                  />
                </div>
                <div
                  className={classNames(
                    "dropdown-content rounded-[5px] bg-white shadow",
                    isActive ? "w-[120px]" : "w-[180px]",
                  )}>
                  <div
                    onMouseDown={() => setIsOpenActivation(true)}
                    className={
                      "flex cursor-pointer items-center gap-2 rounded-[5px] px-[15px] py-[5px] hover:bg-gray-300"
                    }>
                    <BsArchive size={18} className={"text-purple"} />
                    {isActive ? "Archive" : "Move to active"}
                  </div>
                  {isActive && (
                    <div
                      onMouseDown={() => setIsOpenMerge(true)}
                      className={
                        "flex cursor-pointer items-center gap-2 rounded-[5px] px-[15px] py-[5px] hover:bg-gray-300"
                      }>
                      <LuMerge size={18} className={"text-purple"} />
                      Merge
                    </div>
                  )}
                  {!isActive && (
                    <div
                      onMouseDown={() => setIsOpenDelete(true)}
                      className={
                        "flex cursor-pointer items-center gap-2 rounded-[5px] px-[15px] py-[5px] hover:bg-gray-300"
                      }>
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        className={"text-purple w-[18px]"}
                      />
                      Delete
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Routes>
            <Route
              index
              element={
                <Navigate
                  to={{
                    pathname: profile.path,
                  }}
                  replace={true}
                />
              }
            />
            <Route path={profile.path} element={<ProfileComponent />} />
            <Route
              path={works.path}
              element={<WorksComponent contact={contact} />}
            />
            <Route
              path={files.path}
              element={<FilesComponent customerId={customerId} />}
            />
            <Route
              path={time.path}
              element={<TimeComponent contact={contact} />}
            />
            <Route
              path={notes.path}
              element={<NotesComponent contact={contact} />}
            />
            <Route
              path={billing.path}
              element={<BillingComponent contact={contact} />}
            />
            <Route
              path={emails.path + "/*"}
              element={<EmailsComponent contact={contact} />}
            />
            <Route
              path={portal.path}
              element={<PortalComponent customerId={customerId} />}
            />
            <Route path={"*"} element={<>Not found</>} />
          </Routes>
          <div>
            <Outlet />
          </div>
        </div>
      </div>
      {isOpenActivation && (
        <ContactStatusConfirmModal
          onClose={() => setIsOpenActivation(false)}
          onConfirm={() => {
            isActive
              ? archiveContact({ contactId: contact?.contactId || "" })
              : activateContact({ contactId: contact?.contactId || "" });
            setIsOpenActivation(false);
          }}
          contactName={`${contact.firstName} ${contact.lastName}`}
          isActive={isActive}
          isLoading={isLoadingActivate || isLoadingArchive}
        />
      )}
      {isUpdateAvatar && (
        <UpdateAvatarModal
          contact={contact}
          isActive={isUpdateAvatar}
          onClose={() => setIsUpdateAvatar(false)}
        />
      )}
      {isOpenMerge && contact && (
        <MergeContactModal
          customerId={customerId || ""}
          isActive={isOpenMerge}
          onClose={() => setIsOpenMerge(false)}
          name={`${contact?.firstName} ${contact?.lastName}`}
          type={"contact"}
        />
      )}
      {isOpenDelete && (
        <ContactDeleteConfirmModal
          contactName={`${contact.firstName} ${contact.lastName}`}
          isLoading={isLoadingDelete}
          onClose={() => setIsOpenDelete(false)}
          onConfirm={() => {
            if (!contact.customerId) {
              return;
            }

            // Leave the page first (QA batch 8): otherwise the delete makes
            // this page load the deleted client again (500 / 404 errors).
            const deletedName = `${contact.firstName} ${contact.lastName}`;
            navigate("/contacts");
            deleteContact({
              body: [contact.customerId],
              orgId: getLinkedFirm().orgId,
            })
              .unwrap()
              .catch(() =>
                toast(`Couldn't delete ${deletedName}. Please try again.`, {
                  type: "error",
                  pauseOnHover: false,
                }),
              );
          }}
        />
      )}
    </>
  );
};
